"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback } from "react";
import { XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusBanner, StatusBannerRow, StatusBannerContent, statusBannerColorMap } from "@/components/ui/status-banner";
import { useOrgLockoutStatus, useNotifyBillingAdmin } from "@/entities/billing/model";
import type { LockoutStatusResponse } from "@/contexts/billing/core/presentation/rpc/billing-lockout-rpc";
import { formatBillingCurrency } from "./billing-helpers";
interface HardLockBannerProps {
  className?: string;
  /** Pre-fetched lockout status from BillingGate. When provided, skips the
   *  separate useOrgLockoutStatus() RPC call to avoid a double-fetch race. */
  lockoutStatus?: LockoutStatusResponse | null;
}
export function HardLockBanner(t0) {
  const $ = _c(19);
  const {
    className,
    lockoutStatus
  } = t0;
  const {
    data: fetchedStatus,
    isLoading
  } = useOrgLockoutStatus(!lockoutStatus);
  const status = lockoutStatus ?? fetchedStatus;
  if (!lockoutStatus && (isLoading || !status || !status.phase)) {
    return null;
  }
  if (!status || status.phase !== "hard_lock") {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <XCircle className="h-5 w-5 text-white/90 mt-0.5 shrink-0" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== status) {
    t2 = status.isAdmin ? getAdminTitle(status) : "Account restricted \u2014 billing issue needs attention";
    $[1] = status;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <p className="text-sm font-semibold text-white">{t2}</p>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== status) {
    t4 = getSubtext(status);
    $[5] = status;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = <p className="text-xs mt-0.5">{t4}</p>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== status.billingAdminName || $[10] !== status.isAdmin) {
    t6 = <div className="mt-2">{status.isAdmin ? <AdminActions /> : <MemberActions adminName={status.billingAdminName} />}</div>;
    $[9] = status.billingAdminName;
    $[10] = status.isAdmin;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t3 || $[13] !== t5 || $[14] !== t6) {
    t7 = <StatusBannerRow><StatusBannerContent className="flex items-start gap-3">{t1}<div className="flex-1">{t3}{t5}{t6}</div></StatusBannerContent></StatusBannerRow>;
    $[12] = t3;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== className || $[17] !== t7) {
    t8 = <StatusBanner color="red" className={className}>{t7}</StatusBanner>;
    $[16] = className;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
}
function getAdminTitle(status: LockoutStatusResponse): string {
  if (status.amountDueCents != null && status.currency) {
    return `Account restricted — payment of ${formatBillingCurrency(status.amountDueCents, status.currency)} failed`;
  }
  return "Account restricted — payment failed";
}
function getSubtext(status: LockoutStatusResponse): string {
  const parts: string[] = [];
  if (status.attemptCount > 0) {
    parts.push(`${status.attemptCount} payment ${status.attemptCount === 1 ? "attempt" : "attempts"} failed`);
  }
  if (status.paymentMethodBrand && status.paymentMethodLast4) {
    parts.push(`card on file: ${status.paymentMethodBrand} ending in ${status.paymentMethodLast4}`);
  }
  return parts.length > 0 ? parts.join(" · ") : "Update your payment method to restore full access.";
}
function AdminActions() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Button asChild={true} size="sm" variant="outline" className={`h-7 text-xs ${statusBannerColorMap.red.actionButton}`}><a href="/settings/billing">Update payment method</a></Button>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function MemberActions(t0) {
  const $ = _c(12);
  const {
    adminName
  } = t0;
  const {
    mutate: notifyAdmin,
    isPending,
    data
  } = useNotifyBillingAdmin();
  let t1;
  if ($[0] !== notifyAdmin) {
    t1 = () => {
      notifyAdmin();
    };
    $[0] = notifyAdmin;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleNotify = t1;
  const wasRateLimited = data?.rateLimited === true;
  const wasSent = data?.success === true;
  const wasSuppressed = data?.suppressed === true;
  const t2 = isPending || wasSent || wasRateLimited || wasSuppressed;
  const t3 = isPending ? "Sending..." : wasSent ? "Notified" : wasRateLimited ? "Already notified" : wasSuppressed ? "Notifications muted" : `Notify ${adminName ?? "billing admin"}`;
  let t4;
  if ($[2] !== handleNotify || $[3] !== t2 || $[4] !== t3) {
    t4 = <Button variant="outline" size="sm" onClick={handleNotify} disabled={t2} className={`h-7 text-xs ${statusBannerColorMap.red.actionButton}`}>{t3}</Button>;
    $[2] = handleNotify;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== wasSuppressed) {
    t5 = wasSuppressed && <span className="text-xs">They have muted these alerts</span>;
    $[6] = wasSuppressed;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <a href="/settings/billing" className="text-xs underline underline-offset-2">View details</a>;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t4 || $[10] !== t5) {
    t7 = <div className="flex items-center gap-3">{t4}{t5}{t6}</div>;
    $[9] = t4;
    $[10] = t5;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  return t7;
}